/*
 * How a device's copy of each synced key joins the profile's copy at sign-in. Pure functions,
 * tested in mergeRules.test.js. `local` wins ties; nothing is ever dropped by a merge.
 */

export const parse = (text) => { try { return text === null || text === undefined ? undefined : JSON.parse(text); } catch { return undefined; } };
const arr = (v) => (Array.isArray(v) ? v : []);
const obj = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {});

const unionById = (a, b, stamp, max) => {
  const by = new Map();
  [...arr(b), ...arr(a)].forEach((x) => { // local last so it wins ties
    if (!x || !x.id) return;
    const prev = by.get(x.id);
    if (!prev || (x[stamp] || 0) >= (prev[stamp] || 0)) by.set(x.id, x);
  });
  const out = [...by.values()].sort((x, y) => (y[stamp] || 0) - (x[stamp] || 0));
  return max ? out.slice(0, max) : out;
};

/**
 * Two different notes for the same rotation: keep both. The newer one comes first, the older
 * one follows under a divider so nothing written on either device is lost.
 */
const noteText = (n) => String(n.text || '').trim();
const mergeNotes = (a, b) => {
  const [newer, older] = (a.savedAt || 0) >= (b.savedAt || 0) ? [a, b] : [b, a];
  if (!noteText(older)) return newer;
  if (!noteText(newer)) return older;
  if (noteText(newer) === noteText(older)) return newer;
  const divider = '<div><br></div><div><b>Earlier version from another device</b></div>';
  return { html: `${newer.html || ''}${divider}${older.html || ''}`, text: `${newer.text || ''}\n\nEarlier version from another device\n${older.text || ''}`, savedAt: Math.max(a.savedAt || 0, b.savedAt || 0) };
};

export const mergeValues = (key, local, remote) => {
  if (local === undefined) return remote;
  if (remote === undefined) return local;
  if (key === 'education-saved') return unionById(local, remote, 'savedAt');
  if (key === 'education-recent') return unionById(local, remote, 'openedAt', 8);
  if (key === 'education-progress') {
    const out = { ...obj(remote) };
    Object.entries(obj(local)).forEach(([id, p]) => {
      const r = out[id];
      if (!r) { out[id] = p; return; }
      const newer = (p.updatedAt || 0) >= (r.updatedAt || 0) ? p : r;
      out[id] = { ...newer, completed: Boolean(p.completed || r.completed) };
    });
    return out;
  }
  if (key === 'education-attempts') {
    const out = { ...obj(remote) };
    Object.entries(obj(local)).forEach(([col, tests]) => {
      const r = out[col] || {};
      const merged = { ...r };
      Object.entries(obj(tests)).forEach(([which, t]) => {
        const rt = r[which];
        if (!rt) { merged[which] = t; return; }
        const latest = ((t.latest || {}).completedAt || 0) >= ((rt.latest || {}).completedAt || 0) ? t.latest : rt.latest;
        const best = ((t.best || {}).score || 0) >= ((rt.best || {}).score || 0) ? t.best : rt.best;
        merged[which] = { latest, best };
      });
      out[col] = merged;
    });
    return out;
  }
  if (key === 'tee-case-log') {
    const by = new Map(); [...arr(remote), ...arr(local)].forEach((e) => { if (e && e.id) by.set(e.id, e); });
    return [...by.values()].sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')));
  }
  if (/^notes:/.test(key)) return mergeNotes(obj(local), obj(remote));
  // favourites and pins: lists of ids
  if (Array.isArray(local) || Array.isArray(remote)) return [...new Set([...arr(remote), ...arr(local)])];
  return local;
};
